import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import LockOpenOutlinedIcon from "@material-ui/icons/LockOpenOutlined";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import green from "@material-ui/core/colors/green";
import { useHistory } from "../../../shared/hooks/useHistory";

import {
  fetchForgotPassword,
  setForgotPasswordSuccess,
  resetForgotPasswordRequest,
} from "../../actions";

import { validateEmail } from "../../../shared/validateEmail";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  success: {
    color: green[500],
  },
}));

export const ForgotPasswordPage = () => {
  const history = useHistory();
  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [emailValid, setEmailValid] = useState({
    isError: false,
    message: "",
  });
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const { isFetching, isError, isSuccess } = useSelector(
    (state) => state.auth.forgotPassword
  );

  useEffect(() => {
    dispatch(resetForgotPasswordRequest());
  }, [dispatch]);

  useEffect(() => {
    if (isError) {
      setIsOpen(true);
    }
  }, [isError]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const isEmailEmpty = !email.length;
    const isEmailInvalid = !validateEmail(email);

    if (isEmailEmpty || isEmailInvalid) {
      setEmailValid({
        isError: true,
        message: isEmailEmpty
          ? "The email is required"
          : "The email is not correct",
      });
      return;
    }

    setEmailValid({ isError: false, message: "" });
    dispatch(fetchForgotPassword({ email }));
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
  };

  const handleClose = () => setIsOpen(false);

  const handleCancelButton = () => {
    setIsOpen(false);
    dispatch(setForgotPasswordSuccess({ isSuccess: false }));
    history.goBack();
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOpenOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Password Recovery
        </Typography>

        {isSuccess && (
          <Typography component="h1" variant="h6" className={classes.success}>
            Please check your email
          </Typography>
        )}

        <form className={classes.form} noValidate onSubmit={handleSubmit}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="username"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={handleChange}
            error={emailValid.isError}
            helperText={emailValid.message}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={isFetching}
          >
            Send Reset Password
          </Button>

          <Button fullWidth onClick={handleCancelButton}>
            Cancel
          </Button>
        </form>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={isOpen}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="warning">
          Unexpected error
        </Alert>
      </Snackbar>
    </Container>
  );
};
